/**
 * Spec 039 step 3: the company as one view in «Отделы» — a unit's figures and its staff, each
 * with where it works, since when it is idle, how long it worked, what it spent and its order.
 * One request for the whole company, refreshed every 30 s. Spec 040: what a staff member waits
 * for from people and how long — «ждёт разрешения 37 мин» — in red.
 */

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { CompanyView, type UnitHelperOut, type ViewStaff, type ViewUnit } from "@/api/units";
import { ago, Pill } from "@/kit";
import { HELPER_MODE_LABEL } from "@/lib/agents";
import { idleText, shortTokens, STAFF_STATUS, whereText, WORKING, workedText } from "@/lib/company";
import { waitsLine } from "@/lib/feed";

export function useCompanyView() {
  return useQuery({ queryKey: ["company", "view"], queryFn: CompanyView.get, refetchInterval: 30_000 });
}

function Figure({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: "needs" | "blocked" }) {
  const color = tone === "needs" ? "text-p-needs" : tone === "blocked" ? "text-p-blocked" : "text-p-text";
  return (
    <div className="flex flex-col gap-0.5 rounded-card border border-p-border bg-p-surface px-4 py-3">
      <span className="text-meta text-p-text-muted">{label}</span>
      <span className={`text-[20px] font-bold leading-7 ${color}`}>{value}</span>
      {note && <span className="text-meta text-p-text-muted">{note}</span>}
    </div>
  );
}

/** Four figures at the top of a unit: who works, open orders, decisions, the month's spending. */
export function UnitSummary({ unit, staff }: { unit: ViewUnit; staff: ViewStaff[] }) {
  const working = staff.filter((s) => WORKING.has(s.status)).length;
  return (
    // As many as fit the pane, not the window: beside the list a unit may be narrower than a phone.
    <div className="grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-2">
      <Figure label="Работают" value={`${working} из ${staff.length}`} />
      <Figure label="Открытые поручения" value={String(unit.open_orders)} />
      <Figure label="Ждёт решения" value={String(unit.open_decisions)} tone={unit.open_decisions ? "needs" : undefined} />
      <Figure
        label="Расход за месяц"
        value={shortTokens(unit.spent_month)}
        note={unit.limit_month != null ? `лимит ${shortTokens(unit.limit_month)}` : "без лимита"}
        tone={unit.over ? "blocked" : undefined}
      />
    </div>
  );
}

/** Spec 042: a staff member's helpers under its row — cards of their own, in the unit with it. */
export function HelperRows({ helpers }: { helpers: UnitHelperOut[] }) {
  const navigate = useNavigate();
  const shown = helpers.filter((h) => !h.archived);
  if (!shown.length) return null;
  return (
    <ul className="border-t border-p-border bg-p-surface-2" aria-label="Помощники">
      {shown.map((h) => (
        <li key={h.agent_id} className="border-b border-p-border last:border-0">
          <button type="button" onClick={() => navigate(`/agents/${h.agent_id}`)}
            className="flex w-full items-center gap-3 py-2 pl-8 pr-4 text-left press-surface">
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-body text-p-text">
                {h.name}
                <span className="ml-2 text-meta text-p-text-muted">{`помощник · ${HELPER_MODE_LABEL[h.helper_mode]} · v${h.version}`}</span>
              </span>
              {h.purpose_md && <span className="line-clamp-1 text-meta text-p-text-muted">{h.purpose_md}</span>}
            </span>
            {h.run_state === "active" && <Pill tone="done">работает</Pill>}
            {h.run_state === "paused" && <Pill tone="review">на паузе</Pill>}
            <ChevronRight className="h-4 w-4 shrink-0 text-p-text-muted" aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function StaffRow({ s, now, helpers }: { s: ViewStaff; now: number; helpers?: UnitHelperOut[] }) {
  const navigate = useNavigate();
  const status = STAFF_STATUS[s.status];
  const working = WORKING.has(s.status);
  return (
    <li className="border-b border-p-border last:border-0">
      <button type="button" onClick={() => navigate(`/agents/${s.agent_id}`)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left press-surface">
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-[15px] font-semibold leading-5 text-p-text">
            {s.name}
            {s.role === "head" && <span className="ml-2 text-meta font-normal text-p-text-muted">руководитель</span>}
            <span className="ml-2 text-meta font-normal text-p-text-muted">{`v${s.version}`}</span>
          </span>
          <span className="flex flex-wrap items-center gap-1">
            <Pill tone={status.tone}>{status.label}</Pill>
            {s.waits_for && s.waits_since && <Pill tone="blocked">{waitsLine(s.waits_for, s.waits_since, now)}</Pill>}
            {s.waits_people && <Pill tone="needs">ждёт решения</Pill>}
          </span>
          <span className="text-meta text-p-text-muted">
            {working
              ? `${whereText(s)} · ${s.last_action_at ? ago(s.last_action_at, now) : "действий нет"}`
              : idleText(s, now)}
          </span>
          <span className="text-meta text-p-text-muted">
            {`сегодня ${workedText(s.worked_today_min)} · за неделю ${workedText(s.worked_week_min)} · ${shortTokens(s.spent_month)} токенов за месяц`}
          </span>
          {s.order && (
            <span className="line-clamp-1 text-meta text-p-text">{`Поручение №${s.order.number}: ${s.order.title}`}</span>
          )}
        </span>
        <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
      </button>
      {helpers && helpers.length > 0 && <HelperRows helpers={helpers} />}
    </li>
  );
}

/** The unit's staff, its head first; a click opens the staff member's card. Spec 042: each staff
 * member's helpers under it, when the unit gives them (`helpers` by the staff member's id). */
export function StaffTable({ staff, now, helpers }: { staff: ViewStaff[]; now: number; helpers?: Record<string, UnitHelperOut[]> }) {
  if (!staff.length) return <p className="text-meta text-p-text-muted">Сотрудников нет.</p>;
  return (
    <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
      {staff.map((s) => <StaffRow key={s.agent_id} s={s} now={now} helpers={helpers?.[s.agent_id]} />)}
    </ul>
  );
}
